import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';
import { SERVER_ENDPOINT } from 'helpers/constants';
import { getRefreshedToken, forceLogout } from 'helpers/tokenRefresh';
import { RootState } from 'reduxState';
import { setUser } from 'reduxState/store/user/slice';

const baseQuery = fetchBaseQuery({
  baseUrl: `${SERVER_ENDPOINT}/api/`,
  prepareHeaders(headers, { getState }) {
    headers.set('X-WF-UserAgent', 'partner-admin');
    const token = (getState() as RootState).user.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  },
  credentials: 'include',
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await baseQuery(args, api, extraOptions);

  // Handle rate limiting (429 Too Many Requests)
  if (result.error && result.error.status === 429) {
    toast.error("You've made too many requests in a short time. Please wait a moment and try again.");
  }

  // If we get a 401, try to refresh the token
  if (result.error && result.error.status === 401) {
    try {
      // Get a fresh token (shared mutex ensures only one refresh happens at a time)
      // This will automatically handle invalid_grant errors and force logout
      const newToken = await getRefreshedToken();

      // Update the token in Redux state
      const state = api.getState() as RootState;
      api.dispatch(
        setUser({
          token: newToken,
          user: state.user.user,
          email: state.user.email,
          roles: state.user.roles,
        }),
      );

      // Retry the original request with the new token
      result = await baseQuery(args, api, extraOptions);

      // Check for rate limiting on retry as well
      if (result.error && result.error.status === 429) {
        toast.error("You've made too many requests in a short time. Please wait a moment and try again.");
      }
    } catch (error) {
      // Token refresh failed - forceLogout was already called in getRefreshedToken
      console.error('Token refresh failed:', error);
      // Don't redirect here, forceLogout() in tokenRefresh.ts handles it
    }
  }

  return result;
};

const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'feeds',
    'boostedOffers',
    'commissionDownloads',
    'concepts',
    'featuredMerchant',
    'applicationCallback',
    'users',
    'applications',
    'campaigns',
    'actionRewards',
    'ApplicationManagement',
    'PartnerUserConfig',
    'adminApplications',
  ],
  endpoints: () => ({}), // no endpoints will be declared here
});

export default api;
